import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { api, errorMessage } from "@/lib/api";
import type { EquipmentReplacementReceipt } from "@/lib/radnici";

export interface EquipmentNotice {
  message: string;
  replacement?: EquipmentReplacementReceipt;
}

/** Mounted outside equipment rows so refreshing the list preserves undo. */
export function EquipmentReplacementNotice({ workerId, notice, onUndone }: {
  workerId: string;
  notice: EquipmentNotice;
  onUndone: () => void;
}) {
  const { can } = useAuth();
  const [now, setNow] = useState(() => Date.now());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const running = useRef(false);
  const deadline = Date.parse(notice.replacement?.undo_until ?? "");
  const remaining = Math.max(0, Math.ceil((deadline - now) / 1000)) || 0;
  useEffect(() => {
    if (!notice.replacement) return;
    const timer = window.setInterval(() => setNow(Date.now()), 250);
    return () => window.clearInterval(timer);
  }, [notice.replacement]);

  async function undo() {
    if (!notice.replacement || running.current || Date.now() >= deadline) return;
    running.current = true;
    setBusy(true);
    setError("");
    try {
      await api.post(`/radnici/${workerId}/zastitna-oprema/zamjene/${notice.replacement.operation_id}/ponisti`);
      onUndone();
    } catch (cause) {
      setError(errorMessage(cause, "Poništenje nije uspjelo. Pokušajte ponovno."));
    } finally {
      running.current = false;
      setBusy(false);
    }
  }

  return <div className="space-y-2 rounded border p-3" aria-label="Potvrda opreme">
    <p role="status">{notice.message}</p>
    {error && <p role="alert">{error}</p>}
    {notice.replacement && can("znr", "update") && can("znr", "create") && <div className="flex flex-wrap items-center gap-3">
      <Button className="min-h-11" variant="outline" disabled={busy || !remaining} onClick={() => void undo()}>
        {busy ? "Poništavanje…" : `Poništi${remaining ? ` (${remaining} s)` : ""}`}
      </Button>
      {!remaining && <span>Vrijeme za poništavanje je isteklo.</span>}
    </div>}
  </div>;
}
